import React, { useEffect, useState } from "react";

import classnames from "classnames";
import { sortBy } from "lodash";
import simplifyPlanName from "Utils/simplifyPlanName";

import { Body1 } from "Cobalt/BodyText";
import CarrierLogo from "Cobalt/CarrierLogo";
import { Title } from "Cobalt/Header";
import { Surface } from "Cobalt/Surface";
import { Card } from "Common/Card";
import { Grid } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";

import EstimatedCosts from "./Components/EstimatedCosts";
import MoreDetails from "./Components/MoreDetails";
import Overview from "./Components/Overview";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

export const bem = makeBem("MobileComparePlans");

type Props = {
  plans: Array<MedicalPlanPrice>;
  planButtons?: (arg0: MedicalPlanPrice) => React.ReactNode;
  topHeading?: React.ReactNode;
  isInRenewal?: boolean;
  planDeviationProps?: Record<string, any>;
};

export default function Page({ plans, planButtons, topHeading, planDeviationProps, isInRenewal }: Props) {
  const sortedPlans: Array<MedicalPlanPrice> = sortBy(plans, (plan) => (plan ? plan.total_cost : Number.MAX_VALUE)).filter((p) => p != undefined);

  const scrollablePlans = sortedPlans.slice(1);

  const [scrollLeftPosition, setScrollLeftPosition] = useState(undefined);
  const [scrollTopPosition, setScrollTopPosition] = useState(undefined);
  const [defaultPlanHeights, setDefaultPlanHeights] = useState([]);
  const [defaultScrollLeft, setDefaultScrollLeft] = useState(window.outerWidth / 2);

  const [touchState, setTouchState] = useState("");
  const [currentPlan, setCurrentPlan] = useState(0);

  const handleScroll = (e) => {
    if (touchState) {
      const { scrollLeft } = e.target;
      if (Math.abs(scrollLeft - defaultScrollLeft) > 10) {
        setScrollLeftPosition(scrollLeft);

        if (scrollablePlans.length > 2) {
          let prevPlan = currentPlan - 1;
          let nextPlan = currentPlan + 1;

          let prevPlanContainer = document.querySelector(`#compare-plan-${prevPlan}`);

          let nextPlanContainer = document.querySelector(`#compare-plan-${nextPlan}`);

          const currentPlanContainer = document.querySelector(`#compare-plan-${currentPlan}`);

          if (scrollLeftPosition < defaultScrollLeft && currentPlanContainer.getAttribute("peaking") != "prev") {
            if (prevPlan == -1) {
              // If peaking previous plan on first plan, wrap around
              prevPlan = scrollablePlans.length - 1;
              prevPlanContainer = document.querySelector(`#compare-plan-${prevPlan}`);

              prevPlanContainer.style.zIndex = prevPlan;
              nextPlanContainer.style.zIndex = nextPlan;
            } else if (!nextPlanContainer) {
              nextPlan = 0;
              nextPlanContainer = document.querySelector(`#compare-plan-${nextPlan}`);

              prevPlanContainer.style.zIndex = scrollablePlans.length - nextPlan;
              nextPlanContainer.style.zIndex = scrollablePlans.length - prevPlan;
            } else {
              prevPlanContainer.style.zIndex = scrollablePlans.length - prevPlan;
              nextPlanContainer.style.zIndex = scrollablePlans.length - nextPlan;
            }
            currentPlanContainer.setAttribute("peaking", "prev");
          } else if (scrollLeftPosition > defaultScrollLeft && currentPlanContainer.getAttribute("peaking") != "next") {
            if (nextPlan == scrollablePlans.length) {
              // If peaking next plan on last plan, wrap around
              nextPlan = 0;
              nextPlanContainer = document.querySelector(`#compare-plan-${nextPlan}`);

              prevPlanContainer.style.zIndex = scrollablePlans.length - prevPlan;
              nextPlanContainer.style.zIndex = scrollablePlans.length - nextPlan;
            } else if (!prevPlanContainer) {
              prevPlan = scrollablePlans.length - 1;
              prevPlanContainer = document.querySelector(`#compare-plan-${prevPlan}`);

              prevPlanContainer.style.zIndex = nextPlan;
              nextPlanContainer.style.zIndex = prevPlan;
            } else {
              prevPlanContainer.style.zIndex = prevPlan;
              nextPlanContainer.style.zIndex = nextPlan;
            }
            currentPlanContainer.setAttribute("peaking", "next");
          }
        }
      }
    }
  };

  const handleTouchStart = () => {
    setTouchState("touchstart");
    setScrollTopPosition(window.scrollY);
  };

  const handleTouchEnd = () => {
    setTouchState("touchend");
  };

  const resetPosition = (element, index, _defaultScrollLeft) => {
    element.scrollTo({ left: _defaultScrollLeft - index * 4 });
    if (index != currentPlan) {
      element.style.zIndex = scrollablePlans.length - index;
    }
  };

  const adjustHeight = (index) => {
    const outerContainer = document.querySelector("#mobile-compare-container");
    const mainPlanContainer = document.querySelector("#main-plan-container");
    const planHeight = defaultPlanHeights[index] || document.querySelector("#compare-plan-item-0").offsetHeight;

    let newHeight;
    if (mainPlanContainer.offsetHeight > planHeight) {
      newHeight = mainPlanContainer.offsetHeight;
    } else {
      newHeight = planHeight;
    }

    Object.keys(scrollablePlans).forEach((i) => {
      const planItem = document.querySelector(`#compare-plan-item-${i}`);
      if (mainPlanContainer.offsetHeight > planHeight) {
        planItem.style.height = `${planHeight}px`;
      } else {
        planItem.style.height = `${newHeight}px`;
      }
    });

    outerContainer.style.height = `${newHeight + 100}px`;
  };

  useEffect(() => {
    if (touchState && touchState == "touchend" && scrollLeftPosition && scrollTopPosition == window.scrollY) {
      const _currentPlan = currentPlan;

      if (scrollLeftPosition < defaultScrollLeft) {
        let prevPlanContainer;
        let prevPlan;
        if (currentPlan > 0) {
          // focus previous card
          prevPlan = _currentPlan - 1;
          prevPlanContainer = document.querySelector(`#compare-plan-${prevPlan}`);

          const currentPlanContainer = document.querySelector(`#compare-plan-${_currentPlan}`);

          currentPlanContainer.style.zIndex = scrollablePlans.length - _currentPlan;
        } else {
          // focus last card with wrap around
          prevPlan = scrollablePlans.length - 1;
          prevPlanContainer = document.querySelector(`#compare-plan-${prevPlan}`);

          Object.keys(scrollablePlans).forEach((i) => {
            if (i != prevPlan) {
              const planContainer = document.querySelector(`#compare-plan-${i}`);

              planContainer.style.zIndex = i;
            }
          });
        }
        prevPlanContainer.style.zIndex = 100;

        adjustHeight(prevPlan);
        setCurrentPlan(prevPlan);
      } else if (scrollLeftPosition > defaultScrollLeft) {
        let nextPlanContainer;
        let nextPlan;
        if (currentPlan < scrollablePlans.length - 1) {
          // focus next card
          nextPlan = _currentPlan + 1;
          nextPlanContainer = document.querySelector(`#compare-plan-${nextPlan}`);

          const currentPlanContainer = document.querySelector(`#compare-plan-${_currentPlan}`);

          currentPlanContainer.style.zIndex = _currentPlan;
        } else {
          // focus first card with wrap around card
          nextPlan = 0;
          nextPlanContainer = document.querySelector(`#compare-plan-${nextPlan}`);

          Object.keys(scrollablePlans).forEach((i) => {
            if (i != nextPlan) {
              const planContainer = document.querySelector(`#compare-plan-${i}`);

              planContainer.style.zIndex = scrollablePlans.length - i;
            }
          });
        }
        nextPlanContainer.style.zIndex = 100;

        adjustHeight(nextPlan);
        setCurrentPlan(nextPlan);
      }

      // Reset plan card that was scrolled
      const planContainer = document.querySelector(`#compare-plan-${_currentPlan}`);

      planContainer.setAttribute("peaking", "");
      resetPosition(planContainer, _currentPlan, defaultScrollLeft);
      setScrollLeftPosition(undefined);
      setTouchState("");
    }
  }, [touchState]);

  useEffect(() => {
    if (sortedPlans.length > 0) {
      const planHeights = [];
      Object.keys(scrollablePlans).forEach((i) => {
        const planContainer = document.querySelector(`#compare-plan-${i}`);
        const planItem = document.querySelector(`#compare-plan-item-${i}`);

        if (planItem.getAttribute("listener") !== "true") {
          planItem.setAttribute("listener", "true");
          planItem.addEventListener("touchstart", handleTouchStart, {
            passive: true,
          });

          planItem.addEventListener("touchend", handleTouchEnd, {
            passive: true,
          });
        }
        planHeights.push(planItem.offsetHeight);
        resetPosition(planContainer, i, defaultScrollLeft);
      });
      setDefaultPlanHeights(planHeights);
      adjustHeight(0);
      return () => {
        Object.keys(scrollablePlans).forEach((i) => {
          const planItem = document.querySelector(`#compare-plan-item-${i}`);
          planItem.setAttribute("listener", "");
          planItem.removeEventListener("touchstart", handleTouchStart);
          planItem.removeEventListener("touchend", handleTouchEnd);
        });
      };
    }
  }, [sortedPlans.length]);

  const handleResize = () => {
    setDefaultScrollLeft(window.outerWidth / 2);
    Object.keys(scrollablePlans).forEach((i) => {
      const planContainer = document.querySelector(`#compare-plan-${i}`);
      resetPosition(planContainer, i, window.outerWidth / 2);
    });
  };

  useEffect(() => {
    const planContainer = document.querySelector("#mobile-compare-container");
    if (planContainer && planContainer.getAttribute("resizeListener") !== "true") {
      window.addEventListener("resize", handleResize);
      planContainer.setAttribute("resizeListener", "true");
    }
    return () => {
      const planContainer = document.querySelector("#mobile-compare-container");
      planContainer.setAttribute("resizeListener", "");
      window.removeEventListener("resize", handleResize);
    };
  }, [sortedPlans.length]);

  const OverviewRows = Overview({ plans: sortedPlans, isInRenewal });
  const EstimatedCostsRows = EstimatedCosts({
    plans: sortedPlans,
    planDeviationProps,
  });

  const MoreDetailsRows = MoreDetails({ plans: sortedPlans });

  if (sortedPlans.length === 0) {
    return <Grid componentClass={Card}>No plans to show</Grid>;
  }
  return (
    <div id="mobile-compare-container" className={bem()}>
      {topHeading}
      <div className={bem("page-indicator")}>
        {Object.keys(scrollablePlans).map((i) => (
          <div
            key={`page-indicator-${i}`}
            className={classnames(bem("page-indicator", "icon"), {
              [bem("page-indicator", "icon-selected")]: i == currentPlan,
            })}
          >
            <Icon icon="circle" flavor="solid" />
          </div>
        ))}
      </div>
      <div id="main-plan-container" className={bem("main-plan-container")}>
        {sortedPlans[0] && (
          <Surface>
            <Div p={3}>
              <CarrierLogo mt={3} plan={sortedPlans[0]} />
              <Body1 py={3}>{simplifyPlanName(sortedPlans[0])}</Body1>
              {planButtons(sortedPlans[0])}
            </Div>
            {OverviewRows[0]}
            <Title pl={3}>Estimated Costs</Title>
            {Object.entries(EstimatedCostsRows[0]).map(([key, value], j) => (
              <div id={`main-estimated-${j}`}>
                <Body1 pl={3} pb={3}>
                  {key}
                </Body1>
                {value}
              </div>
            ))}

            <Title pl={3}>Coverage Details</Title>
            {Object.entries(MoreDetailsRows[0]).map(([key, value], j) => (
              <div key={`main-details-${j}`}>
                <Body1 pl={3} pb={3}>
                  {key}
                </Body1>
                {value}
              </div>
            ))}
          </Surface>
        )}
      </div>
      {scrollablePlans.map(
        (plan, i) =>
          plan && (
            <div
              onScroll={handleScroll}
              id={`compare-plan-${i}`}
              className={classnames(bem("scrollable-plan-container"), {
                [bem("scrollable-plan-container", "active")]: i == currentPlan && scrollablePlans.length > 1,
                [bem("scrollable-plan-container", "inactive")]: i != currentPlan || scrollablePlans.length == 1,
              })}
            >
              <div className={bem("scrollable-plan-inner")}>
                <div className={bem("scrollable-plan-spacer")} />
                <div
                  id={`compare-plan-item-${i}`}
                  className={classnames(bem("scrollable-plan-item"), {
                    [bem("scrollable-plan-item", "active")]: i == currentPlan,
                  })}
                >
                  <Surface>
                    <Div p={3}>
                      <CarrierLogo mt={3} plan={plan} />
                      <Body1 py={3}>{simplifyPlanName(plan)}</Body1>
                      {planButtons(plan)}
                    </Div>
                    {OverviewRows[i + 1]}
                    <Title pl={3}>Estimated Costs</Title>
                    {Object.entries(EstimatedCostsRows[i + 1]).map(([key, value]) => (
                      <>
                        <Body1 pl={3} pb={3}>
                          {key}
                        </Body1>
                        {value}
                      </>
                    ))}

                    <Title pl={3}>Coverage Details</Title>
                    {Object.entries(MoreDetailsRows[i + 1]).map(([key, value]) => (
                      <>
                        <Body1 pl={3} pb={3}>
                          {key}
                        </Body1>
                        {value}
                      </>
                    ))}
                  </Surface>
                </div>
              </div>
            </div>
          ),
      )}
    </div>
  );
}
